import React, {useState} from 'react'
import {useTranslation} from '@wix/yoshi-flow-editor'
import {isAfter} from 'date-fns'
import {Text} from 'wix-ui-tpa/cssVars'
import {setHMToDate} from '@wix/table-reservations-lib/schedule'

import {LayoutSize} from '../LayoutSizeProvider/types'
import {TimeOption} from '../../utils/timeOptions'
import {NotAvailableTimeSlotNotification} from '../NotAvailableTimeSlotNotification'
import {SelectTimeSlotError} from '../SelectTimeSlotError'
import {TagsListProps} from '../TagsList'
import {TimeSlotsByDate} from '../../types/TimeSlots'
import {getMediumDate} from '../../utils/dateString'

import {st, classes} from './TimeSlotsListExtended.st.css'
import {timeSlotsListExtendedDataHooks} from './constants'

export interface TimeSlotsListExtendedProps {
  regionalSettings?: string
  items: TimeSlotsByDate
  partySize: number
  selectedDate?: Date
  selectedTimeOption?: TimeOption
  selectedTimeSlot?: Date
  showSelectTimeSlotError: boolean
  showNoAvailableTimeSlotNotification: boolean
  onSelectTimeSlot: (item: TimeSlotsByDate[string][number]) => void
  layoutSize: LayoutSize
  titleTag: string
  renderTagsList: (props: TagsListProps) => React.ReactNode
  titleClassName?: string
  overdoneLinkClassName?: string
  notificationClassName?: string
}

export const TimeSlotsListExtended: React.FC<TimeSlotsListExtendedProps> = ({
  layoutSize,
  regionalSettings,
  items = {},
  partySize,
  selectedDate,
  selectedTimeOption,
  selectedTimeSlot,
  showNoAvailableTimeSlotNotification,
  showSelectTimeSlotError,
  titleTag,
  onSelectTimeSlot,
  renderTagsList,
  titleClassName,
  overdoneLinkClassName,
  notificationClassName,
}) => {
  const {t} = useTranslation()

  const dates = Object.keys(items)
  const [isFullyOpened, setIsFullyOpened] = useState<boolean>(dates?.length <= 3)
  const datesToShow = isFullyOpened ? dates : dates.slice(0, 3)

  if (!Object.values(items)?.length) {
    return null
  }

  return (
    <div className={st(classes.root)} data-hook={timeSlotsListExtendedDataHooks.root()}>
      {showNoAvailableTimeSlotNotification && selectedDate && selectedTimeOption ? (
        <NotAvailableTimeSlotNotification
          date={setHMToDate(
            selectedDate,
            selectedTimeOption?.data.hours ?? 0,
            selectedTimeOption?.data.minutes ?? 0,
          )}
          partySize={partySize}
          regionalSettings={regionalSettings}
          showTime={!!selectedTimeOption}
          textClassName={notificationClassName}
        />
      ) : null}

      {showSelectTimeSlotError && <SelectTimeSlotError />}

      <Text tagName={titleTag} className={st(classes.title, titleClassName)}>
        {t('uou-reservations.reservations.time-slot-other-dates.text')}
      </Text>

      {datesToShow
        .sort((a, b) => {
          const aDate = new Date(a)
          const bDate = new Date(b)

          return isAfter(aDate, bDate) ? 1 : -1
        })
        .map((key, index) => (
          <section className={st(classes.section)} key={index}>
            {renderTagsList({
              layoutSize,
              label: getMediumDate(new Date(key), regionalSettings),
              timeSlots: items?.[key] ?? [],
              onSelectTimeSlot,
              regionalSettings,
              selectedTimeSlot,
              extended: true,
            })}
          </section>
        ))}

      {!isFullyOpened ? (
        <a
          href="#"
          className={st(classes.overdoneLink, overdoneLinkClassName)}
          onClick={(e) => {
            e.preventDefault()

            setIsFullyOpened(true)
          }}
        >
          {t('uou-reservations.reservations.time-slot-other-dates.cta')}
        </a>
      ) : null}
    </div>
  )
}
